import { ComponentType, ReactNode, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors, r8, s4, s8 } from '@ardoq/design-tokens';
import {
  CheckCircleIcon,
  CloseIcon,
  ErrorIcon,
  IconSize,
  InfoIcon,
  WarningIcon,
} from '@ardoq/icons';
import { caption, text2 } from '@ardoq/typography';
import { SmallGhostButton } from '@ardoq/button';

export type PeekNotificationBannerStatus =
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'subtle';

type StatusConfig = {
  backgroundColor: string;
  textColor: string;
  iconColor: string;
  Icon: ComponentType<{ iconSize?: IconSize; color?: string }>;
  role: 'alert' | 'status';
};

const STATUS_CONFIG: Record<PeekNotificationBannerStatus, StatusConfig> = {
  error: {
    backgroundColor: colors.surfaceError,
    textColor: colors.textError,
    iconColor: colors.iconError,
    Icon: ErrorIcon,
    role: 'alert',
  },
  warning: {
    backgroundColor: colors.surfaceWarning,
    textColor: colors.textWarning,
    iconColor: colors.iconWarning,
    Icon: WarningIcon,
    role: 'status',
  },
  info: {
    backgroundColor: colors.surfaceInfo,
    textColor: colors.textInfo,
    iconColor: colors.iconInfo,
    Icon: InfoIcon,
    role: 'status',
  },
  success: {
    backgroundColor: colors.surfaceSuccess,
    textColor: colors.textSuccess,
    iconColor: colors.iconSuccess,
    Icon: CheckCircleIcon,
    role: 'status',
  },
  subtle: {
    backgroundColor: colors.surfaceStatusNeutral,
    textColor: colors.textModerate,
    iconColor: colors.textSubtle,
    Icon: InfoIcon,
    role: 'status',
  },
};

export const PEEK_IN_DURATION_MS = 250;
export const PEEK_OUT_DURATION_MS = 200;
const PEEK_IN_DURATION = `${PEEK_IN_DURATION_MS}ms`;
const PEEK_OUT_DURATION = `${PEEK_OUT_DURATION_MS}ms`;
export const PEEK_BOTTOM_OVERLAP = s8;

const peekIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const peekOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const BannerContainer = styled.div<{
  $backgroundColor: string;
  $textColor: string;
  $isSubtle: boolean;
  $isClosing: boolean;
}>`
  ${({ $isSubtle }) => ($isSubtle ? caption : text2)};
  position: absolute;
  bottom: calc(100% - ${PEEK_BOTTOM_OVERLAP});
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: ${s4};
  padding: ${s4} ${s8} calc(${s4} + ${PEEK_BOTTOM_OVERLAP});
  border-radius: ${r8} ${r8} 0 0;
  background: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $textColor }) => $textColor};

  animation-name: ${({ $isClosing }) => ($isClosing ? peekOut : peekIn)};
  animation-duration: ${({ $isClosing }) =>
    $isClosing ? PEEK_OUT_DURATION : PEEK_IN_DURATION};
  animation-timing-function: ${({ $isClosing }) =>
    $isClosing ? 'ease-in' : 'ease-out'};
  animation-fill-mode: ${({ $isClosing }) =>
    $isClosing ? 'forwards' : 'none'};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Message = styled.div`
  flex: 1;
  overflow-wrap: anywhere;
`;

export type PeekNotificationBannerProps = {
  isVisible: boolean;
  status: PeekNotificationBannerStatus;
  children: ReactNode;
  /** When provided, renders a dismiss button. Respond by setting `isVisible` to `false`. */
  onDismiss?: () => void;
  /** Accessible name for the dismiss button. Set this when several banners are on screen at once. */
  dismissLabel?: string;
  /** Fired once the peek-out animation has finished and the banner has stopped rendering. */
  onPeekOutComplete?: VoidFunction;
  /** Override the default status icon. Pass `null` to render no icon. */
  icon?: ReactNode | null;
  className?: string;
};

/**
 * A compact notification that peeks in over the top edge of a container — for
 * small, component-level spaces (e.g. above a card), not page-level banners.
 *
 * The banner is absolutely positioned against its nearest positioned ancestor,
 * so the **consumer must wrap the banner and the host component in an element
 * with `position: relative`**. The banner sits above the host and takes no
 * layout space. For the banner to tuck cleanly behind the host's top edge as it
 * animates, give the host an opaque background and a `z-index` above the banner.
 *
 * @example
 * ```tsx
 * <div style={{ position: 'relative' }}>
 *   <PeekNotificationBanner
 *     isVisible={Boolean(error)}
 *     status="error"
 *     onDismiss={clearError}
 *   >
 *     {error}
 *   </PeekNotificationBanner>
 *   <Card style={{ position: 'relative', zIndex: 1 }}>…</Card>
 * </div>
 * ```
 */
export const PeekNotificationBanner = ({
  isVisible,
  status,
  children,
  onDismiss,
  dismissLabel = 'Dismiss',
  onPeekOutComplete,
  icon,
  className,
}: PeekNotificationBannerProps) => {
  const [isRendered, setIsRendered] = useState(isVisible);
  // kept out of the effect deps so an inline callback cannot re-arm the timer
  const onPeekOutCompleteRef = useRef(onPeekOutComplete);
  onPeekOutCompleteRef.current = onPeekOutComplete;

  useEffect(() => {
    if (isVisible) {
      setIsRendered(true);
      return;
    }
    const timer = setTimeout(() => {
      setIsRendered(false);
      onPeekOutCompleteRef.current?.();
    }, PEEK_OUT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isVisible]);

  if (!isRendered) return null;

  const { backgroundColor, textColor, iconColor, Icon, role } =
    STATUS_CONFIG[status];
  const isSubtle = status === 'subtle';
  const iconSize = isSubtle ? IconSize.EXTRA_SMALL : IconSize.SMALL;

  return (
    <BannerContainer
      className={className}
      role={role}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      $isSubtle={isSubtle}
      $isClosing={!isVisible}
    >
      {icon === undefined ? (
        <Icon iconSize={iconSize} color={iconColor} />
      ) : (
        icon
      )}
      <Message>{children}</Message>
      {onDismiss && (
        <SmallGhostButton aria-label={dismissLabel} onClick={onDismiss}>
          <CloseIcon iconSize={iconSize} />
        </SmallGhostButton>
      )}
    </BannerContainer>
  );
};
